import React from "react";
import {
    Col,
    Drawer,
    Form,
    Select,
    Row, TimePicker, Switch, Radio,
} from "antd";
import moment from "moment";
import { Button } from "src/components/actions/button";
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg";
import { useAppData } from "src/store/app-data";
import styled from "styled-components";
import {validateSecondsIsZero} from "../../utils/helpers";

function formatPointTime(value) {
    if (value == null || value === "") return "—";
    const base =
        typeof value === "string"
            ? value.trim().split(".")[0]
            : String(value).trim().split(".")[0];
    let m = moment(base, "HH:mm:ss", true);
    if (m.isValid()) return m.format("HH:mm:ss");
    m = moment(value);
    return m.isValid() ? m.format("HH:mm:ss") : "—";
}

const events = [
    {
        label: "Увеличить время",
        value: "add"
    },
    {
        label: "Уменьшить время",
        value: "«reduce»"
    },
    {
        label: "Сбросить до стандартного",
        value: "reset"
    },
];

const tableEvents = [
    {
        label: "Увеличить время",
        value: "add_table"
    },
    {
        label: "Уменьшить время",
        value: "reduce_table"
    },
    {
        label: "Сбросить до стандартного",
        value: "reset_table"
    },
];

export function MassEditTimeDrawer({
                                       title,
                                       onClose,
                                       onSave,
                                       pointOption,
                                       fetchPointById,
                                       isPickUp = false,
                                       isTable = false,
                                       withKitchenType = false
                                   }) {
    const layoutConfig = useAppData((state) => state.layoutConfig);
    const resourceAccess = useAppData((state) => state.resourceAccess);
    const [form] = Form.useForm();
    const [fetchedPoint, setFetchedPoint] = React.useState(null);
    const [loadingPointTimes, setLoadingPointTimes] = React.useState(false);

    const allowedPointIds = resourceAccess?.point ?? [];
    const hasPointRestriction = Array.isArray(allowedPointIds) && allowedPointIds.length > 0;
    const allowedPointIdSet = React.useMemo(
        () => new Set((allowedPointIds ?? []).map((id) => Number(id))),
        [allowedPointIds]
    );
    const filteredPointOption = React.useMemo(
        () =>
            hasPointRestriction
                ? (pointOption ?? []).filter((opt) => allowedPointIdSet.has(Number(opt.value)))
                : pointOption ?? [],
        [pointOption, hasPointRestriction, allowedPointIdSet]
    );

    React.useEffect(() => {
        if (hasPointRestriction) {
            form.setFieldsValue({ applyToAll: false });
        }
    }, [hasPointRestriction, form]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    async function onSubmit(values) {
        if (values.time && typeof values.time.format === "function") {
            values.time = values.time.format("HH:mm:ss");
        }

        const payload = {
            time: values.time,
            event: values.event
        }

        if (isPickUp) {
            payload.pointId = values.pointId
            payload.onlyKitchen = values.onlyKitchen ? 1 : 0
            payload.applyToAll = values.applyToAll ? 1 : 0
        } else if (withKitchenType) {
            payload.onlyKitchen = values.onlyKitchen ? 1 : 0
        }

        try {
            await onSave(payload, (values.applyToAll || isPickUp) ? undefined : values.pointId)

            form.resetFields();
            onClose();
        } catch (e) {
            console.error(e);
        }
    }

    const selectedEvent = Form.useWatch("event", form);
    const applyToAll = Form.useWatch("applyToAll", form);
    const pointId = Form.useWatch("pointId", form);

    React.useEffect(() => {
        if (!isPickUp || !pointId || applyToAll || !fetchPointById) {
            setFetchedPoint(null);
            setLoadingPointTimes(false);
            return;
        }
        let cancelled = false;
        setFetchedPoint(null);
        setLoadingPointTimes(true);
        fetchPointById(pointId)
            .then((item) => {
                console.log(item)
                if (!cancelled) setFetchedPoint(item);
            })
            .finally(() => {
                if (!cancelled) setLoadingPointTimes(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isPickUp, pointId, applyToAll, fetchPointById]);

    const showCurrentTimes = isPickUp && !applyToAll && pointId && fetchPointById;

    const currentTotalTime = fetchedPoint
        ? isTable
            ? fetchedPoint.timeForTable
            : fetchedPoint.timeForHurryUp
        : null;
    const currentKitchenTime = fetchedPoint
        ? isTable
            ? fetchedPoint.timeForKitchenTableTime
            : fetchedPoint.timeForKitchenHurryUp
        : null;

    return (
        <DrawerEdit
            width={layoutConfig.isMobile ? "90%" : "80%"}
            title={title}
            onClose={handleClose}
            open
        >
            <FormEdit form={form}
                      layout={"vertical"}
                      onFinish={onSubmit}
                      initialValues={{ applyToAll: false, onKitchen: false }}
            >
                {!hasPointRestriction && (
                    <StyledFormItem
                        key={"massEdit_applyAll"}
                        label={"Применить ко всем точкам"}
                        name={"applyToAll"}
                        valuePropName="checked"
                    >
                        <Switch />
                    </StyledFormItem>
                )}

                {!applyToAll &&
                    <StyledFormItem
                        key={"massEdit_pointId"}
                        label={"Tочка"}
                        name={"pointId"}
                        rules={[{ required: true, message: "Выберите точку!" }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            options={filteredPointOption}
                            placeholder="Начните вводить название..."
                        />
                    </StyledFormItem>
                }

                {showCurrentTimes && (
                    <CurrentTimesSummary>
                        {loadingPointTimes ? (
                            <CurrentTimesMuted>Загрузка текущих значений…</CurrentTimesMuted>
                        ) : (
                            <>
                                <CurrentTimeRow>
                                    <span>Текущее время общее</span>
                                    <strong>{currentTotalTime}</strong>
                                </CurrentTimeRow>
                                <CurrentTimeRow>
                                    <span>Текущее время кухня</span>
                                    <strong>{currentKitchenTime}</strong>
                                </CurrentTimeRow>
                            </>
                        )}
                    </CurrentTimesSummary>
                )}

                {(isPickUp || withKitchenType) && (
                    <StyledFormItem
                        key={"massEdit_onlyKitchen"}
                        label={"Тип ожидания"}
                        name={"onlyKitchen"}
                        initialValue={false}
                        rules={[{ required: true, message: "Выберите тип ожидания" }]}
                    >
                        <Radio.Group buttonStyle="solid" value={form.onlyKitchen || false}>
                            <Radio.Button value={false}>Общее</Radio.Button>
                            <Radio.Button value={true}>Кухня</Radio.Button>
                        </Radio.Group>
                    </StyledFormItem>
                )}

                <StyledFormItem
                    key={"massEdit_event"}
                    label={"Действие"}
                    name={"event"}
                    rules={[{ required: true, message: "Выберите действие!" }]}
                >
                    <Select
                        options={!isTable ? events : tableEvents}
                        placeholder="Выберите действие"
                    />
                </StyledFormItem>

                {(selectedEvent !== "reset" && selectedEvent !== "reset_table") && (
                    <StyledFormItem
                        key={"massEdit_time"}
                        label={"Время на сколько изменить"}
                        name={"time"}
                        rules={[
                            { required: true, message: "Выберите время!" },
                            { validator: validateSecondsIsZero },
                        ]}
                    >
                        <TimePicker style={{ width: "100%" }} format="HH:mm:ss" minuteStep={1} />
                    </StyledFormItem>
                )}

                <ButtonRow>
                    <ButtonCol>
                        <Button
                            danger
                            onClick={() => form.resetFields()}
                            icon={<RotateClockwiseSvg />}
                        >
                            Сбросить
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button onClick={handleClose} icon={<CloseSvg />}>
                            Закрыть
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                            Сохранить
                        </Button>
                    </ButtonCol>
                </ButtonRow>
            </FormEdit>
        </DrawerEdit>
    );
}

const DrawerEdit = styled(Drawer)``;
const FormEdit = styled(Form)``;
const CurrentTimesSummary = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;
const CurrentTimeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  &:first-of-type {
    margin-top: 0;
  }
`;
const CurrentTimesMuted = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
`;
const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`;
const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`;
const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`;
